import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";
import API from "../services/api";
import "../styles/Auth.css";

export default function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [registerMethod, setRegisterMethod] = useState("email"); // 'email' or 'phone'
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phoneNumber: ""
  });
  const [showPassword, setShowPassword] = useState(false);

  // Google Login Hook
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await API.post("/auth/google", {
          access_token: tokenResponse.access_token
        });
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(res.data.user));

        if (res.data.user.detailsRequired) {
          toast.success("Please complete your profile details.");
          navigate("/complete-profile");
        } else {
          toast.success("Account created via Google!");
          navigate("/");
        }
      } catch (err) {
        console.error(err);
        toast.error("Google Sign Up Failed");
      }
    },
    onError: () => toast.error("Google Sign Up Failed"),
  });

  // Handle Input Change
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const generateStrongPassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
    let pwd = "";
    // Ensure at least one of each required type
    pwd += "ABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(Math.floor(Math.random() * 26));
    pwd += "abcdefghijklmnopqrstuvwxyz".charAt(Math.floor(Math.random() * 26));
    pwd += "0123456789".charAt(Math.floor(Math.random() * 10));
    pwd += "!@#$%^&*()_+".charAt(Math.floor(Math.random() * 12));

    // Fill the rest to make it 14 characters
    for (let i = 0; i < 10; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Shuffle the string
    pwd = pwd.split('').sort(() => 0.5 - Math.random()).join('');

    setForm({ ...form, password: pwd });
    setShowPassword(true);
    toast.success("Strong password generated!");
  };

  // Submit Logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (registerMethod === "email") {
        const res = await API.post("/auth/register", {
          name: form.name,
          email: form.email,
          password: form.password
        });
        if (res.data.otpSent) {
          toast.success("Account created! Please verify your email.");
          navigate("/verify-otp", { state: { email: form.email, from: "/dashboard" } });
        } else {
          toast.success("Registration successful! Please log in.");
          navigate("/login");
        }
      } else {
        // Phone Register (Same as Login - Check or Create)
        const res = await API.post("/auth/send-otp-phone", { phoneNumber: form.phoneNumber });
        if (res.data.otpSent) {
          toast.success("OTP Sent to Phone!");
          console.log("Simulated OTP:", res.data.dev_otp);
          navigate("/verify-otp", { state: { phoneNumber: form.phoneNumber, from: "/" } });
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <form className="auth-form" onSubmit={handleSubmit}>

        {/* Header Section */}
        <div className="auth-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="8.5" cy="7" r="4" />
            <line x1="20" y1="8" x2="20" y2="14" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
        </div>

        <h2 className="auth-title">Create Account</h2>
        <p className="auth-subtitle">
          Already have an account? <Link to="/login">Sign In</Link>
        </p>

        {/* Tabs */}
        <div className="auth-tabs" style={{ marginTop: '1.5rem' }}>
          <button
            type="button"
            className={`auth-tab ${registerMethod === 'email' ? 'active' : ''}`}
            onClick={() => setRegisterMethod('email')}
          >
            Email
          </button>
          <button
            type="button"
            className={`auth-tab ${registerMethod === 'phone' ? 'active' : ''}`}
            onClick={() => setRegisterMethod('phone')}
          >
            Phone
          </button>
        </div>

        {/* Google Button */}
        {registerMethod === 'email' && (
          <>
            <button
              type="button"
              className="google-btn"
              onClick={() => googleLogin()}
              style={{ marginTop: '1.5rem' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continue with Google
            </button>

            <div className="auth-divider">
              <span>OR</span>
            </div>

            {/* Name Input */}
            <div className="form-group">
              <label className="input-label">Full Name <span>*</span></label>
              <input
                name="name"
                placeholder="Enter your full name"
                value={form.name}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            {/* Email Input */}
            <div className="form-group">
              <label className="input-label">Email Address <span>*</span></label>
              <input
                name="email"
                type="email"
                placeholder="Enter your email"
                value={form.email}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            {/* Password Input */}
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="input-label" style={{ margin: 0 }}>Password <span>*</span></label>
                <button
                  type="button"
                  onClick={generateStrongPassword}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Auto-suggest strong password
                </button>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password (min 6 chars)"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#64748b',
                    display: 'flex'
                  }}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94m9.9.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  )}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                Use a strong password: min 8 chars, 1 uppercase, 1 number, 1 special char.
              </p>
            </div>
          </>
        )}

        {registerMethod === 'phone' && (
          /* Phone Input */
          <div className="form-group" style={{ marginTop: '2rem' }}>
            <label className="input-label">Phone Number <span>*</span></label>
            <input
              name="phoneNumber"
              type="tel"
              placeholder="+91 XXXXX XXXXX"
              value={form.phoneNumber}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>
        )}

        <button type="submit" disabled={loading} className="primary-btn-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? (
            <>
              <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
              Processing...
            </>
          ) : (registerMethod === 'email' ? "Sign Up" : "Send OTP")}
        </button>

        <p className="auth-footer-text">
          By continuing, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}
